import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from "@/constants";

// Lobby/exterior poster — small (~80 kb) for instant LCP on mobile.
const HERO_POSTER =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920&q=70&auto=format&fit=crop";
const HERO_POSTER_MOBILE =
  "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=60&auto=format&fit=crop";

// Desktop: compressed landscape welcome video (~1.6 MB, self-hosted, 1280×720 H.264).
const HERO_VIDEO_DESKTOP = "/video/hero-desktop.mp4";

// Mobile: compressed portrait reel (~534 kb, self-hosted, 540×960 H.264) — perfect for phones.
const HERO_VIDEO_MOBILE = "/video/hero-mobile.mp4";

export const HeroSection = () => {
  const videoRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const conn =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    // Respect Save-Data hint everywhere
    if (conn?.saveData) return;
    // Skip video entirely on truly slow connections
    if (conn?.effectiveType && /^(2g|slow-2g)$/.test(conn.effectiveType)) return;

    const pick = () => (isMobile ? HERO_VIDEO_MOBILE : HERO_VIDEO_DESKTOP);

    // Defer until idle so the poster paints instantly
    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const id = idle(() => setVideoSrc(pick()));
    return () => {
      if (window.cancelIdleCallback) window.cancelIdleCallback(id);
    };
  }, []);

  return (
    <section
      className="relative h-screen overflow-hidden pt-40 sm:pt-32 md:pt-36"
      data-testid="hero-section"
    >
      <picture>
        <source media="(max-width: 768px)" srcSet={HERO_POSTER_MOBILE} />
        <img
          src={HERO_POSTER}
          alt="Best Western Joliet Inn & Suites"
          className="absolute inset-0 w-full h-full object-cover"
          fetchPriority="high"
          decoding="async"
        />
      </picture>
      {videoSrc && (
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover"
          poster={HERO_POSTER}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/50 z-10" />
      <div className="relative z-20 h-full flex flex-col items-center justify-center px-4 text-center">
        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-7xl text-white font-bold mb-6">
          Best Western
          <br />
          Joliet Inn & Suites
        </h1>
        <p className="text-white/90 text-lg md:text-xl max-w-2xl mb-8">
          Experience comfort and convenience near Chicago's best attractions
        </p>
        <div className="flex flex-col sm:flex-row gap-4">
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-now-link"
          >
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-6 rounded-full text-lg font-semibold">
              Book Your Stay
            </Button>
          </a>
          <a href="#rooms" data-testid="hero-explore-rooms-link">
            <Button
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white px-8 py-6 rounded-full text-lg font-semibold"
            >
              Explore Rooms
            </Button>
          </a>
        </div>
        <a
          href="tel:+18157307500"
          className="mt-5 inline-flex items-center gap-2 text-white/85 hover:text-[#D4AF37] text-sm font-medium transition-colors"
          data-testid="hero-call-link"
          aria-label="Call our front desk at (815) 730-7500, open 24 hours a day, 7 days a week"
        >
          <span className="opacity-80">Or call to speak with our front desk</span>
          <span className="text-[#D4AF37] font-semibold">(815) 730-7500</span>
          <span className="hidden sm:inline opacity-70">· open 24/7</span>
        </a>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-black/40 py-4 z-20">
        <p className="text-center text-white text-lg md:text-xl font-['Playfair_Display'] italic">
          Your home away from home...
        </p>
      </div>
    </section>
  );
};
